import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import PinIcon from '@mui/icons-material/Pin';
import {
  LocalizationProvider,
  MobileDateTimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ptBR } from "@mui/x-date-pickers/locales";
import dayjs, { Dayjs } from "dayjs";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AnsweringClient,
  IInstrument,
  IUserResponse,
} from "../clients/answeringClient";

export interface CreateCouponCodeFormState {
  code: string;
  assignedUserUids: string[];
  assignedInstrumentIds: number[];
  usageLimit: string;
  usageType: "global" | "user";
  startsAt: string;
  endsAt: string;
  active: boolean;
}

interface CreateCouponCodeDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  submitting: boolean;
  error?: string;
  couponName?: string;
  formValues: CreateCouponCodeFormState;
  onChange: <K extends keyof CreateCouponCodeFormState>(
    field: K,
    value: CreateCouponCodeFormState[K]
  ) => void;
  title: string;
  submitLabel: string;
  submittingLabel: string;
  disableCodeInput?: boolean;
  defaultSelectedUsers?: IUserResponse[] | null;
  couponStartsAt?: string | null;
  couponEndsAt?: string | null;
}

const mergeUsers = (
  primary: IUserResponse[],
  secondary: IUserResponse[]
): IUserResponse[] => {
  const seen = new Set<string>();
  const result: IUserResponse[] = [];
  const addUser = (user: IUserResponse | null) => {
    if (!user || !user.uid || seen.has(user.uid)) {
      return;
    }
    seen.add(user.uid);
    result.push(user);
  };
  primary.forEach(addUser);
  secondary.forEach(addUser);
  return result;
};

const serializeUserIds = (uids: string[]): string =>
  uids
    .filter((uid): uid is string => Boolean(uid))
    .map((uid) => uid.trim())
    .filter((uid) => uid.length > 0)
    .sort()
    .join("|");

type InstrumentOption = Pick<IInstrument, "id" | "title">;

const mergeInstruments = (
  primary: InstrumentOption[],
  secondary: InstrumentOption[]
): InstrumentOption[] => {
  const seen = new Set<number>();
  const result: InstrumentOption[] = [];
  const addInstrument = (instrument: InstrumentOption | null | undefined) => {
    if (!instrument) {
      return;
    }
    const id = Number(instrument.id);
    if (!Number.isFinite(id) || seen.has(id)) {
      return;
    }
    seen.add(id);
    result.push({
      id,
      title: instrument.title ?? "",
    });
  };
  primary.forEach(addInstrument);
  secondary.forEach(addInstrument);
  return result;
};

const serializeInstrumentIds = (ids: number[]): string =>
  ids
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0)
    .sort((a, b) => a - b)
    .join("|");

export const CreateCouponCodeDialog: React.FC<
  CreateCouponCodeDialogProps
> = ({
  open,
  onClose,
  onSubmit,
  submitting,
  error,
  couponName,
  formValues,
  onChange,
  title,
  submitLabel,
  submittingLabel,
  disableCodeInput = false,
  defaultSelectedUsers = null,
  couponStartsAt = null,
  couponEndsAt = null,
}) => {
  const answeringClient = useMemo(() => new AnsweringClient(), []);
  const [userInput, setUserInput] = useState("");
  const [userOptions, setUserOptions] = useState<IUserResponse[]>([]);
  const [userLoading, setUserLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<IUserResponse[]>([]);
  const listFetchTokenRef = useRef(0);
  const skipFetchRef = useRef(false);
  const lastUserSelectionRef = useRef<string>("");
  const [instrumentInput, setInstrumentInput] = useState("");
  const [instrumentOptions, setInstrumentOptions] = useState<InstrumentOption[]>([]);
  const [instrumentLoading, setInstrumentLoading] = useState(false);
  const [selectedInstruments, setSelectedInstruments] = useState<InstrumentOption[]>([]);
  const instrumentListFetchTokenRef = useRef(0);
  const instrumentSkipFetchRef = useRef(false);
  const lastInstrumentSelectionRef = useRef<string>("");
  const hasAppliedDefaultRef = useRef(false);
  const couponStartsAtDate = useMemo(() => {
    if (!couponStartsAt) {
      return null;
    }
    const parsed = dayjs(couponStartsAt);
    return parsed.isValid() ? parsed : null;
  }, [couponStartsAt]);
  const couponEndsAtDate = useMemo(() => {
    if (!couponEndsAt) {
      return null;
    }
    const parsed = dayjs(couponEndsAt);
    return parsed.isValid() ? parsed : null;
  }, [couponEndsAt]);
  const startsAtValue = useMemo(() => {
    if (!formValues.startsAt) {
      return null;
    }
    const parsed = dayjs(formValues.startsAt);
    return parsed.isValid() ? parsed : null;
  }, [formValues.startsAt]);
  const endsAtValue = useMemo(() => {
    if (!formValues.endsAt) {
      return null;
    }
    const parsed = dayjs(formValues.endsAt);
    return parsed.isValid() ? parsed : null;
  }, [formValues.endsAt]);
  const startsAtMinDateTime = couponStartsAtDate ?? undefined;
  const startsAtMaxDateTime = couponEndsAtDate ?? undefined;
  const endsAtMinDateTime = useMemo(() => {
    const candidates: Dayjs[] = [];
    if (couponStartsAtDate) {
      candidates.push(couponStartsAtDate);
    }
    if (startsAtValue) {
      candidates.push(startsAtValue);
    }
    if (candidates.length === 0) {
      return undefined;
    }
    return candidates.reduce((latest, current) =>
      current.isAfter(latest) ? current : latest
    );
  }, [couponStartsAtDate, startsAtValue]);
  const endsAtMaxDateTime = couponEndsAtDate ?? undefined;

  const updateUserInput = useCallback((value: string, skipFetch = false) => {
    if (skipFetch) {
      skipFetchRef.current = true;
    }
    setUserInput(value);
  }, []);

  const updateInstrumentInput = useCallback((value: string, skipFetch = false) => {
    if (skipFetch) {
      instrumentSkipFetchRef.current = true;
    }
    setInstrumentInput(value);
  }, []);

  const getUserLabel = useCallback((user: IUserResponse) => {
    const names = [user.firstName, user.lastName].filter(
      (value): value is string => Boolean(value)
    );
    const fullName = names.join(" ").trim();

    if (fullName && user.email) {
      return `${fullName} (${user.email})`;
    }
    if (user.email) {
      return user.email;
    }
    if (fullName) {
      return fullName;
    }
    return "Usuário sem identificação";
  }, []);

  const getInstrumentLabel = useCallback((instrument: InstrumentOption) => {
    if (!instrument) {
      return "Instrumento";
    }
    const title = instrument.title?.trim();
    if (title) {
      return title;
    }
    if (Number.isFinite(instrument.id)) {
      return `Instrumento #${instrument.id}`;
    }
    return "Instrumento";
  }, []);

  const fetchUsers = useCallback(
    async (query: string) => {
      const accessToken = localStorage.getItem("AccessToken");
      if (!accessToken) {
        setUserOptions(selectedUsers);
        return;
      }

      const fetchToken = listFetchTokenRef.current + 1;
      listFetchTokenRef.current = fetchToken;
      setUserLoading(true);

      try {
        const response = await answeringClient.searchAllUsers(accessToken, {
          filter: query || undefined,
          limit: 20,
          page: 1,
        });

        if (listFetchTokenRef.current !== fetchToken) {
          return;
        }

        const results = (response.data?.data ?? []) as IUserResponse[];
        setUserOptions(() => mergeUsers(selectedUsers, results));
      } catch (error) {
        if (listFetchTokenRef.current !== fetchToken) {
          return;
        }
        console.error("Failed to search users for coupon code dialog:", error);
        setUserOptions(selectedUsers);
      } finally {
        if (listFetchTokenRef.current === fetchToken) {
          setUserLoading(false);
        }
      }
    },
    [answeringClient, selectedUsers]
  );

  const fetchInstruments = useCallback(
    async (query: string) => {
      const accessToken = localStorage.getItem("AccessToken");
      if (!accessToken) {
        setInstrumentOptions(selectedInstruments);
        return;
      }

      const fetchToken = instrumentListFetchTokenRef.current + 1;
      instrumentListFetchTokenRef.current = fetchToken;
      setInstrumentLoading(true);

      try {
        const response = await answeringClient.getManagedInstrumentsSearch(accessToken, {
          filter: query || undefined,
          limit: 20,
          page: 1,
        });

        if (instrumentListFetchTokenRef.current !== fetchToken) {
          return;
        }

        const results =
          (response.data?.data ?? []).map(
            (instrument): InstrumentOption => ({
              id: instrument.id,
              title: instrument.title ?? "",
            })
          ) ?? [];
        setInstrumentOptions(() => mergeInstruments(selectedInstruments, results));
      } catch (error) {
        if (instrumentListFetchTokenRef.current !== fetchToken) {
          return;
        }
        console.error(
          "Failed to search instruments for coupon code dialog:",
          error
        );
        setInstrumentOptions(selectedInstruments);
      } finally {
        if (instrumentListFetchTokenRef.current === fetchToken) {
          setInstrumentLoading(false);
        }
      }
    },
    [answeringClient, selectedInstruments]
  );

  useEffect(() => {
    if (!open) {
      setSelectedUsers([]);
      setUserOptions([]);
      updateUserInput("", true);
      listFetchTokenRef.current += 1;
      setUserLoading(false);
      lastUserSelectionRef.current = "";
      setSelectedInstruments([]);
      setInstrumentOptions([]);
      updateInstrumentInput("", true);
      instrumentListFetchTokenRef.current += 1;
      setInstrumentLoading(false);
      lastInstrumentSelectionRef.current = "";
      hasAppliedDefaultRef.current = false;
      return;
    }

    const defaultUsers =
      defaultSelectedUsers?.filter(
        (user): user is IUserResponse => Boolean(user && user.uid)
      ) ?? [];

    const assignedUids =
      formValues.assignedUserUids?.map((uid) => uid.trim()).filter(Boolean) ??
      [];
    const assignedKey = serializeUserIds(assignedUids);

    if (
      assignedUids.length > 0 &&
      assignedKey === lastUserSelectionRef.current
    ) {
      return;
    }

    if (defaultUsers.length > 0 && !hasAppliedDefaultRef.current) {
      setSelectedUsers(defaultUsers);
      setUserOptions((prev) => mergeUsers(defaultUsers, prev));
      updateUserInput("", true);
      lastUserSelectionRef.current = serializeUserIds(
        defaultUsers.map((user) => user.uid)
      );
      hasAppliedDefaultRef.current = true;
      return;
    }

    if (assignedUids.length === 0) {
      setSelectedUsers([]);
      updateUserInput("", true);
      lastUserSelectionRef.current = "";
      return;
    }

    let isActive = true;
    const accessToken = localStorage.getItem("AccessToken");
    if (!accessToken) {
      updateUserInput("", true);
      return;
    }

    setUserLoading(true);
    Promise.all(
      assignedUids.map((uid) =>
        answeringClient
          .searchAllUsers(accessToken, {
            filter: uid,
            limit: 5,
            page: 1,
          })
          .then(
            (response) => response.data?.data?.[0] as IUserResponse | undefined
          )
          .catch((error) => {
            console.error(
              "Failed to preselect user for coupon code dialog:",
              error
            );
            return undefined;
          })
      )
    )
      .then((users) => {
        if (!isActive) {
          return;
        }
        const hydratedUsers = assignedUids.map((uid) => {
          const found = users.find((user) => user?.uid === uid);
          return (
            found ?? {
              uid,
              email: "",
              firstName: "",
              lastName: "",
            }
          );
        });
        setSelectedUsers(hydratedUsers);
        setUserOptions((prev) => mergeUsers(hydratedUsers, prev));
        lastUserSelectionRef.current = serializeUserIds(
          hydratedUsers.map((user) => user.uid)
        );
      })
      .finally(() => {
        if (!isActive) {
          return;
        }
        setUserLoading(false);
        updateUserInput("", true);
      });

    return () => {
      isActive = false;
    };
  }, [
    open,
    defaultSelectedUsers,
    formValues.assignedUserUids,
    answeringClient,
    updateUserInput,
    updateInstrumentInput,
  ]);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (skipFetchRef.current) {
      skipFetchRef.current = false;
      return;
    }

    const handler = window.setTimeout(() => {
      fetchUsers(userInput.trim());
    }, 300);

    return () => {
      window.clearTimeout(handler);
    };
  }, [fetchUsers, open, userInput]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const assignedInstrumentIds =
      formValues.assignedInstrumentIds?.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0) ??
      [];
    const assignedKey = serializeInstrumentIds(assignedInstrumentIds);

    if (assignedInstrumentIds.length === 0) {
      setSelectedInstruments([]);
      updateInstrumentInput("", true);
      lastInstrumentSelectionRef.current = "";
      return;
    }

    if (assignedKey === lastInstrumentSelectionRef.current) {
      return;
    }

    const accessToken = localStorage.getItem("AccessToken");
    if (!accessToken) {
      setSelectedInstruments([]);
      updateInstrumentInput("", true);
      lastInstrumentSelectionRef.current = "";
      return;
    }

    let isActive = true;
    setInstrumentLoading(true);
    answeringClient
      .getAllManagedInstruments(accessToken)
      .then((response) => {
        if (!isActive) {
          return;
        }
        const allInstruments = Array.isArray(response.data) ? response.data : [];
        const map = new Map<number, InstrumentOption>(
          allInstruments.map((instrument) => [
            instrument.id,
            { id: instrument.id, title: instrument.title ?? "" },
          ])
        );
        const hydrated = assignedInstrumentIds.map(
          (id): InstrumentOption =>
            map.get(id) ?? {
              id,
              title: `Instrumento #${id}`,
            }
        );
        setSelectedInstruments(hydrated);
        setInstrumentOptions((prev) => mergeInstruments(hydrated, prev));
        lastInstrumentSelectionRef.current = serializeInstrumentIds(
          hydrated.map((instrument) => instrument.id)
        );
        updateInstrumentInput("", true);
      })
      .catch((error) => {
        if (!isActive) {
          return;
        }
        console.error(
          "Failed to preselect instruments for coupon code dialog:",
          error
        );
        const fallback = assignedInstrumentIds.map(
          (id): InstrumentOption => ({
            id,
            title: `Instrumento #${id}`,
          })
        );
        setSelectedInstruments(fallback);
        setInstrumentOptions((prev) => mergeInstruments(fallback, prev));
        lastInstrumentSelectionRef.current = serializeInstrumentIds(
          fallback.map((instrument) => instrument.id)
        );
        updateInstrumentInput("", true);
      })
      .finally(() => {
        if (!isActive) {
          return;
        }
        setInstrumentLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [answeringClient, formValues.assignedInstrumentIds, open, updateInstrumentInput]);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (instrumentSkipFetchRef.current) {
      instrumentSkipFetchRef.current = false;
      return;
    }

    const handler = window.setTimeout(() => {
      fetchInstruments(instrumentInput.trim());
    }, 300);

    return () => {
      window.clearTimeout(handler);
    };
  }, [fetchInstruments, instrumentInput, open]);

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      variant="modal"
    >
      <DialogTitle variant="modal">
        <PinIcon />
        {title}
      </DialogTitle>
      <DialogContent variant="modal">
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          localeText={
            ptBR.components.MuiLocalizationProvider.defaultProps.localeText
          }
        >
          <Box display="flex" flexDirection="column" gap={2}>
            {couponName && (
              <Typography variant="subtitle2" color="text.secondary">
                Cupom: {couponName}
              </Typography>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              label="Código"
              value={formValues.code}
              onChange={(event) => {
                const sanitizedValue = event.target.value
                  .replace(/[^a-zA-Z0-9]/g, "")
                  .toLowerCase();
                onChange("code", sanitizedValue);
              }}
              fullWidth
              required
              autoFocus
              disabled={submitting || disableCodeInput}
            />

            <Autocomplete
              multiple
              disableCloseOnSelect
              filterSelectedOptions
              options={userOptions}
              value={selectedUsers}
              onChange={(_, newValue, reason) => {
                if (reason === "clear") {
                  setSelectedUsers([]);
                  onChange("assignedUserUids", []);
                  lastUserSelectionRef.current = "";
                  setUserOptions((prev) => mergeUsers([], prev));
                  updateUserInput("", true);
                  return;
                }
                setSelectedUsers(newValue);
                onChange(
                  "assignedUserUids",
                  newValue.map((user) => user.uid)
                );
                lastUserSelectionRef.current = serializeUserIds(
                  newValue.map((user) => user.uid)
                );
                setUserOptions((prev) => mergeUsers(newValue, prev));
                updateUserInput("", true);
              }}
              inputValue={userInput}
              onInputChange={(_, value, reason) => {
                if (reason === "input") {
                  updateUserInput(value);
                } else if (reason === "clear") {
                  updateUserInput("", true);
                }
              }}
              getOptionLabel={getUserLabel}
              noOptionsText={
                userInput.trim()
                  ? "Nenhum usuário encontrado"
                  : "Digite um nome ou e-mail para buscar"
              }
              loading={userLoading}
              disabled={submitting}
              isOptionEqualToValue={(option, value) => option.uid === value.uid}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.uid}
                    size="small"
                    label={getUserLabel(option)}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Usuários vinculados"
                  placeholder="Pesquisar por nome ou e-mail"
                  helperText="Opcional. Selecione um ou mais usuários que poderão utilizar este código."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {userLoading && (
                          <CircularProgress color="inherit" size={20} />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <Autocomplete
              multiple
              disableCloseOnSelect
              filterSelectedOptions
              options={instrumentOptions}
              value={selectedInstruments}
              onChange={(_, newValue, reason) => {
                if (reason === "clear") {
                  setSelectedInstruments([]);
                  onChange("assignedInstrumentIds", []);
                  lastInstrumentSelectionRef.current = "";
                  setInstrumentOptions((prev) => mergeInstruments([], prev));
                  updateInstrumentInput("", true);
                  return;
                }
                setSelectedInstruments(newValue);
                onChange(
                  "assignedInstrumentIds",
                  newValue.map((instrument) => instrument.id)
                );
                lastInstrumentSelectionRef.current = serializeInstrumentIds(
                  newValue.map((instrument) => instrument.id)
                );
                setInstrumentOptions((prev) => mergeInstruments(newValue, prev));
                updateInstrumentInput("", true);
              }}
              inputValue={instrumentInput}
              onInputChange={(_, value, reason) => {
                if (reason === "input") {
                  updateInstrumentInput(value);
                } else if (reason === "clear") {
                  updateInstrumentInput("", true);
                }
              }}
              getOptionLabel={getInstrumentLabel}
              noOptionsText={
                instrumentInput.trim()
                  ? "Nenhum instrumento encontrado"
                  : "Digite o nome do instrumento"
              }
              loading={instrumentLoading}
              disabled={submitting}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    size="small"
                    label={getInstrumentLabel(option)}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Instrumentos vinculados"
                  placeholder="Pesquisar por nome do instrumento"
                  helperText="Opcional. Selecione um ou mais instrumentos que aceitarão este código."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {instrumentLoading && (
                          <CircularProgress color="inherit" size={20} />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <Box display="flex" flexDirection="column" gap={0.5}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formValues.usageType === "user"}
                    onChange={(event) => {
                      const nextType = event.target.checked ? "user" : "global";
                      onChange("usageType", nextType);
                      if (event.target.checked) {
                        onChange("usageLimit", "");
                      }
                    }}
                    disabled={submitting}
                  />
                }
                label="Limitar a 1 uso por usuário"
              />
              <Typography variant="caption" color="text.secondary">
                Ative para liberar um uso por pessoa ou desative para controlar um limite total.
              </Typography>
            </Box>

            {formValues.usageType === "global" && (
              <TextField
                label="Limite de uso"
                type="number"
                value={formValues.usageLimit}
                onChange={(event) => onChange("usageLimit", event.target.value)}
                fullWidth
                inputProps={{ min: 1, step: 1 }}
                helperText="Opcional. Deixe em branco para não definir limite."
                disabled={submitting}
              />
            )}

            <MobileDateTimePicker
              label="Início da vigência"
              format="D/M/YYYY HH:mm"
              ampm={false}
              value={startsAtValue}
              referenceDate={dayjs().hour(0o0).minute(0o1)}
              onChange={(value: Dayjs | null) => {
                if (value && value.isValid()) {
                  onChange("startsAt", value.toISOString());
                } else {
                  onChange("startsAt", "");
                }
              }}
              minDateTime={startsAtMinDateTime}
              maxDateTime={startsAtMaxDateTime}
              slotProps={{
                textField: {
                  fullWidth: true,
                  disabled: submitting,
                },
                actionBar: {
                  actions: ["clear", "cancel", "accept"],
                },
              }}
            />

            <MobileDateTimePicker
              label="Fim da vigência"
              format="D/M/YYYY HH:mm"
              ampm={false}
              value={endsAtValue}
              referenceDate={dayjs().hour(23).minute(59)}
              onChange={(value: Dayjs | null) => {
                if (value && value.isValid()) {
                  onChange("endsAt", value.toISOString());
                } else {
                  onChange("endsAt", "");
                }
              }}
              minDateTime={endsAtMinDateTime}
              maxDateTime={endsAtMaxDateTime}
              slotProps={{
                textField: {
                  fullWidth: true,
                  disabled: submitting,
                },
                actionBar: {
                  actions: ["clear", "cancel", "accept"],
                },
              }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formValues.active}
                  onChange={(event) => onChange("active", event.target.checked)}
                  disabled={submitting}
                />
              }
              label="Código ativo"
            />
          </Box>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions variant="modal">
        <Button onClick={onClose} color="error" variant="outlined" disabled={submitting}>
          Cancelar
        </Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          color="primary"
          disabled={submitting}
        >
          {submitting ? submittingLabel : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
